import { getCurrentUser } from '@aws-amplify/auth';
import { Theme as AmplifyTheme, ThemeProvider as AmplifyThemeProvider, Authenticator, Image, translations, View } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import '@fontsource/poppins/100.css';
import '@fontsource/poppins/200.css';
import '@fontsource/poppins/300.css';
import '@fontsource/poppins/400.css';
import '@fontsource/poppins/500.css';
import '@fontsource/poppins/600.css';
import '@fontsource/poppins/700.css';
import '@fontsource/poppins/800.css';
import '@fontsource/poppins/900.css';
import CssBaseline from '@mui/material/CssBaseline';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Hub, I18n } from 'aws-amplify/utils';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AuthenticatedApp from './AuthenticatedApp.tsx';
import logo from './assets/logo.png';
import { setAuthToken } from './common/api.ts';
import ColorModeContext from './components/ui/ColorModeContext.tsx';
import { fetchUserProfile } from './store/authSlice.ts';
import { AppDispatch, RootState } from './store/store.ts';

const App = () => {
  const [mode, setMode] = React.useState<'light' | 'dark'>('light');
  const dispatch: AppDispatch = useDispatch();
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);

  const colorMode = React.useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  const theme = React.useMemo(
    () =>
      createTheme({
        typography: {
          fontFamily: 'Poppins, sans-serif',
          fontWeightRegular: 500,
          fontSize: 14,
          body1: {
            fontSize: '1rem',
          },
        },
        components: {
          MuiStepLabel: {
            styleOverrides: {
              root: {
                '& .Mui-active.MuiStepLabel-alternativeLabel': {
                  fontWeight: 'bold',
                  fontSize: '1rem',
                  color: '#6B6BFC',
                },
                '& .MuiStepLabel-label': {
                  fontSize: '1rem',
                  fontWeight: 500,
                },
                '& .Mui-completed.MuiStepLabel-alternativeLabel': {
                  fontWeight: 'bold',
                  color: '#6B6BFC',
                },
                '& .MuiSvgIcon-root': {
                  marginY: '-3px',
                  width: '30px',
                  height: '30px',
                },
              },
            },
          },
          MuiTab: {
            styleOverrides: {
              root: {
                // Styles for the Tab label
                fontSize: '1rem',
                fontWeight: 500,
                '&.Mui-selected': {
                  fontWeight: 'bold', // Bold font when selected
                  color: '#6B6BFC', // Change color when selected
                },
              },
            },
          },
        },
        palette: {
          mode,
          ...(mode === 'light'
            ? {
                primary: {
                  main: '#6B6BFC',
                  light: '#e6eaff',
                },
                text: {
                  primary: '#555555',
                  secondary: '#888888',
                },
                background: {
                  default: '#fafafa',
                  paper: '#ffffff',
                },
              }
            : {
                primary: {
                  main: '#6B6BFC',
                  light: '#b3b7ff',
                },
                text: {
                  primary: '#eeeeee',
                  secondary: '#bbbbbb',
                },
              }),
        },
      }),
    [mode],
  );

  useEffect(() => {
    // console.debug('setupListener');
    let hubListenerCancelToken = undefined;
    const setupListener = () => {
      hubListenerCancelToken = Hub.listen('auth', ({ payload }) => {
        switch (payload.event) {
          case 'signedIn':
            //console.debug('user have been signedIn successfully.');
            void dispatch(fetchUserProfile());
            break;
          case 'signedOut':
            // remove the auth token from the API
            setAuthToken(undefined);
            // console.debug('user have been signedOut successfully.');
            break;
        }
      });
    };
    setupListener();
    return hubListenerCancelToken;
  }, [dispatch]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        //console.debug('Fetch user Profile');
        await getCurrentUser();
        await dispatch(fetchUserProfile());
      } catch (error) {
        // console.debug('User not logged in');
        // this is ok, the user is not logged in
      }
    };
    void loadUser();
  }, [dispatch]);

  // Authenticator components

  const authTheme: AmplifyTheme = {
    name: 'Auth Example Theme',
    tokens: {
      components: {
        button: {
          primary: {
            backgroundColor: '#6B6BFC',
          },
        },
      },
    },
  };

  I18n.putVocabularies({
    fr: {
      'username is required to resetPassword': 'Votre email est requis pour réinitialiser votre mot de passe',
      'User does not exist.': "Cet utilisateur n'existe pas",
      'Incorrect username or password.': 'Nom d’utilisateur ou mot de passe incorrect.',
    },
  });
  I18n.setLanguage('fr');

  I18n.putVocabularies(translations);

  const components = {
    Header() {
      return (
        <View textAlign="center">
          <Image alt="Amplify logo" src={logo} />
        </View>
      );
    },
  };

  const formFields = {
    forgotPassword: {
      username: {
        label: 'Email',
        placeholder: 'Saissisez votre email',
      },
    },
    signIn: {
      username: {
        label: 'Email',
        placeholder: 'Saissisez votre email',
      },
    },
  };

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AmplifyThemeProvider theme={authTheme}>
          <Authenticator components={components} formFields={formFields} hideSignUp loginMechanisms={['email']} variation="modal">
            {isAuthenticated && <AuthenticatedApp />}
          </Authenticator>
        </AmplifyThemeProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default App;
